import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import check from "../../Asset/check.svg";
import fillCheck from "../../Asset/fill check.svg";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonLoading from "../../component/ButtonLoading";
import { trackEvent } from "../../lib/mixpanel";

const PricingSection = () => {
  const user = useSelector(selectUser);
  const [isAnnual, setIsAnnual] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubscription = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/create-subscription-session`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user?.EmailId,
          name: `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim(),
          // ✅ UPDATED (new plan codes)
          plan: isAnnual ? "Pro_Annual" : "Pro",
        }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        // ignore non-json
      }

      if (!res.ok) {
        const msg =
          payload?.message ||
          payload?.error ||
          res.statusText ||
          "Failed to create subscription session.";
        toast.error(msg);
        return;
      }

      if (payload?.success && payload?.clientSecret) {
        trackEvent("subscription_started", {
          plan_type: isAnnual ? "Pro_Annual" : "Pro",
          billing_cycle: isAnnual ? "annual" : "monthly",
        });
        // ✅ Keep your same checkout route IDs
        navigate(
          `/checkout/${isAnnual ? "N9sJ0qWz7eB1" : "aJ3d8Lk29vX4"}/${payload.clientSecret}`
        );
        return;
      }

      toast.error(
        payload?.message || payload?.error || "Unexpected server response."
      );
    } catch (err) {
      toast.error(err?.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full  flex justify-center items-center md:px-4 p-0">
      <div className="flex flex-col lg:flex-row lg:gap-0 items-center gap-6 max-w-5xl w-full">
        {/* Free Plan */}
        <div className="flex-1  md:rounded-s-3xl rounded-3xl bg-[#F6FBFE] lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3  p-10 lg:pr-16 lg:-mr-8">
          <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
            ZT Basic
          </h1>
          <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
            Free
          </h2>
          <p className="md:text-[1.375rem] text-base leading-none font-medium font-inter">
            Start your ZT journey:
          </p>
          <ul className="space-y-4 ">
            {[
              "15 minutes audio transcription per week",
              "Personal Dictionary",
              "Basic Prompt Selection",
              "Translate and Transcribe audio",
            ].map((feature) => (
              <li
                key={feature}
                className="flex items-center md:text-[1.076rem] text-sx  font-inter text-[#000000] gap-2"
              >
                <ReactSVG
                  src={check}
                  beforeInjection={(svg) => {
                    svg.setAttribute(
                      "class",
                      "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                    );
                  }}
                />
                {feature}
              </li>
            ))}
          </ul>

          <NavLink
            to={"/download"}
            className="md:mt-8 mt-6 w-full rounded-full border bg-transparent border-[#3AB1F6]  hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF]  transition"
          >
            <span className="block w-full rounded-full bg-white py-3 text-center font-inter md:text-[1.076rem] text-sx font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] transition-all duration-300 hover:bg-none hover:text-white hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF]">
              Get Started Today
            </span>
          </NavLink>
        </div>

        {/* Pro Plan */}
        <div className="flex-1 rounded-3xl bg-[#EBF7FE] border border-[#407BFF] text-black lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3 md:shadow-2xl shadow-xl p-10 relative overflow-hidden">
          {/* Toggle */}
          <div className="flex justify-center w-full">
            <div className="bg-white p-2 rounded-full flex gap-1 w-full md:text-[1.22rem] text-sm  font-semibold font-inter">
              <button
                onClick={() => setIsAnnual(false)}
                className={`flex-1  py-3 rounded-full text-sm font-medium transition ${
                  !isAnnual
                    ? " bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                    : "text-gray-700"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`flex-1  py-3 rounded-full text-sm font-medium transition ${
                  isAnnual
                    ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                    : "text-gray-700"
                }`}
              >
                Annual
              </button>
            </div>
          </div>

          <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
            ZT Pro
          </h1>
          <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
            ${isAnnual ? "8.25" : "9.99"}{" "}
            <span className="md:text-[1.365rem] text-base font-inter">
              /month
            </span>
          </h2>
          <p className="md:text-[1.375rem] text-base  font-medium font-inter">
            Get all premium ZT features. Everything in basic plus:
          </p>
          <ul className="space-y-4 ">
            {[
              "Unlimited words per week",
              "Unlimited audio transcription per week",
              "Custom prompt creation",
              "Early access to new features",
            ].map((feature) => (
              <li
                key={feature}
                className="flex items-center md:text-[1.076rem] text-sx font-inter text-[#000000] gap-2"
              >
                <ReactSVG
                  src={fillCheck}
                  beforeInjection={(svg) => {
                    svg.setAttribute(
                      "class",
                      "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                    );
                  }}
                />
                {feature}
              </li>
            ))}
          </ul>

          <button
            onClick={handleSubscription}
            disabled={isLoading}
            className={`mt-8 ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            } w-full rounded-full border bg-transparent hover:border-[#3AB1F6] border-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out `}
          >
            <span className=" w-full flex items-center justify-center rounded-full  py-3 text-center font-inter md:text-[1.068rem] text-sx font-semibold text-white ">
              {isLoading ? (
                <ButtonLoading text="Subscribing..." />
              ) : (
                "Get Started Today"
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
